import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FolderIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';
import AssignmentMeaningReview, {
  countValidMeaningSelections,
  initialMeaningSelections,
  toggleMeaningSelection,
  validMeaningSelections,
} from './AssignmentMeaningReview';
import AssignmentGenerationProgress, {
  useAssignmentGenerationProgress,
  waitForAssignmentMaterialization,
} from './AssignmentGenerationProgress';
import VocabularyActionDialog from './VocabularyActionDialog';
import TrainingModeSelector, { TrainingModeBadge } from './TrainingModeSelector';
import { paginateItems } from './assignmentPagination';
import {
  TRAINING_MODE_MEANING,
  buildTrainingModePayload,
  getPrimaryTrainingMode,
  getTrainingModes,
  normalizeTrainingModes,
} from '../lib/trainingModes';
import { IELTS_DEFAULT_TARGET_BAND, serializeIeltsSpeakingConfig } from '../lib/ieltsSpeaking';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const requestJson = async (url, options = {}) => {
  const response = await fetch(url, { ...options, headers: { ...authHeaders(), ...(options.headers || {}) } });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.message || 'Request failed.');
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return { payload, status: response.status };
};

const entryKey = (entry) => `${String(entry.word || '').toLowerCase()}::${entry.sense_id || 'primary'}`;

const RESULT_ACTIONS = [
  ['assign', 'Assign now', 'Give these exact meanings to the class.'],
  ['new_assign', 'Create list and assign', 'Keep a reusable teaching list and assign it now.'],
  ['append_assign', 'Add to a list and assign', 'Append new meanings to an existing teaching list, then assign.'],
  ['save', 'Save as teaching list', 'Keep the reviewed meanings for later without assigning them.'],
];

const ATTACHED_PRACTICES = [
  ['dictation', 'Dictation Loop'],
  ['meaning_sprint', '7-Second Meaning'],
  ['sudden_sprint', 'Lightning Challenge'],
  ['pronunciation', 'Pronunciation Practice'],
  ['adaptive_reading', 'Adaptive Reading'],
  ['ielts_speaking', 'IELTS Speaking Practice'],
];

const TEACHING_LIST_PAGE_SIZE = 8;
const SAVED_ENTRY_PAGE_SIZE = 12;

const PracticeChecklist = ({ selected, onChange, disabled, readingMode, onReadingModeChange, ieltsConfig, onIeltsConfigChange, t }) => (
  <div className="mt-4 rounded border border-indigo-200 bg-indigo-50 p-4">
    <h4 className="font-bold text-gray-950">{t('Also assign practice')}</h4>
    <p className="mt-1 text-sm text-gray-600">{t('These practices use the exact meanings you just confirmed.')}</p>
    <div className="mt-3 flex flex-wrap gap-3">
      {ATTACHED_PRACTICES.map(([value, label]) => (
        <label key={value} className="inline-flex items-center gap-2 rounded border border-indigo-200 bg-white px-3 py-2 text-sm">
          <input type="checkbox" checked={selected.includes(value)} onChange={() => onChange(value)} disabled={disabled} className="h-4 w-4 text-indigo-600" />
          <span>{t(label)}</span>
        </label>
      ))}
    </div>
    {selected.includes('adaptive_reading') && (
      <fieldset className="mt-3 border-t border-indigo-200 pt-3">
        <legend className="text-sm font-semibold text-gray-800">{t('Reading mode')}</legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {[
            ['custom', 'Adaptive practice'],
            ['mock', 'IELTS simulation'],
          ].map(([value, label]) => (
            <label key={value} className={`inline-flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-sm ${readingMode === value ? 'border-indigo-500 bg-white text-indigo-800' : 'border-indigo-200 bg-indigo-50 text-gray-700'}`}>
              <input type="radio" name="attached-reading-mode" value={value} checked={readingMode === value} onChange={() => onReadingModeChange(value)} disabled={disabled} className="h-4 w-4 text-indigo-600" />
              <span>{t(label)}</span>
            </label>
          ))}
        </div>
      </fieldset>
    )}
    {selected.includes('ielts_speaking') && (
      <fieldset className="mt-3 grid gap-3 border-t border-indigo-200 pt-3 sm:grid-cols-2">
        <legend className="text-sm font-semibold text-gray-800">{t('IELTS speaking settings')}</legend>
        <label className="block text-sm font-medium text-gray-800 sm:col-span-2">
          {t('Speaking question')} <span className="font-normal text-gray-500">({t('optional')})</span>
          <textarea value={ieltsConfig?.question || ''} onChange={(event) => onIeltsConfigChange?.({ question: event.target.value })} className="mt-1 min-h-16 w-full resize-y rounded border border-gray-300 bg-white p-2" placeholder={t('Leave empty for a general speaking assessment.')} />
        </label>
        <label className="block text-sm font-medium text-gray-800">
          {t('Part')} <span className="font-normal text-gray-500">({t('optional')})</span>
          <select value={ieltsConfig?.part || ''} onChange={(event) => onIeltsConfigChange?.({ part: event.target.value })} className="mt-1 w-full rounded border border-gray-300 bg-white p-2">
            <option value="">{t('Not specified')}</option>
            <option value="1">{t('Part 1')}</option>
            <option value="2">{t('Part 2')}</option>
            <option value="3">{t('Part 3')}</option>
          </select>
        </label>
        <label className="block text-sm font-medium text-gray-800">
          {t('Suggested answer time')}
          <input type="number" min="15" max="300" step="5" value={ieltsConfig?.suggested_duration_seconds || 90} onChange={(event) => onIeltsConfigChange?.({ suggested_duration_seconds: event.target.value })} className="mt-1 w-full rounded border border-gray-300 bg-white p-2" />
        </label>
        <label className="block text-sm font-medium text-gray-800 sm:col-span-2">
          {t('Target band')}
          <input type="number" min="0" max="9" step="0.5" value={ieltsConfig?.target_band ?? IELTS_DEFAULT_TARGET_BAND} onChange={(event) => onIeltsConfigChange?.({ target_band: event.target.value })} className="mt-1 w-full rounded border border-gray-300 bg-white p-2 sm:max-w-48" />
          <span className="mt-1 block text-xs font-normal text-gray-600">{t('The result will show whether the student reached this predicted band.')}</span>
        </label>
        {ieltsConfig?.question?.trim() ? (
          <p className="rounded border border-indigo-200 bg-white px-3 py-2 text-xs leading-5 text-indigo-900 sm:col-span-2">
            {t('A clear question uses the strict examiner simulation. The examiner reads it aloud before recording.')}
          </p>
        ) : (
          <label className="inline-flex items-start gap-2 text-sm text-gray-800 sm:col-span-2">
            <input type="checkbox" checked={Boolean(ieltsConfig?.strict_timer)} onChange={(event) => onIeltsConfigChange?.({ strict_timer: event.target.checked })} className="mt-0.5 h-4 w-4 text-indigo-600" />
            <span><span className="font-semibold">{t('Strict timer')}</span><span className="mt-0.5 block text-xs text-gray-600">{t('Stop the microphone automatically at the suggested time.')}</span></span>
          </label>
        )}
        <p className="text-xs leading-5 text-indigo-900 sm:col-span-2">{t('Both question and Part can be empty. The student will see a general speaking assessment and no question will be generated.')}</p>
      </fieldset>
    )}
  </div>
);

export const AssignmentSourceToolbar = ({ mode, onModeChange, onCreateTeachingFolder, t }) => (
  <div
    data-testid="assignment-source-toolbar"
    className="assignment-source-toolbar inline-flex max-w-full flex-wrap items-center gap-1 self-start rounded-md border border-gray-200 bg-gray-50 p-1"
  >
    <button
      type="button"
      onClick={() => onModeChange('saved')}
      className={`min-w-0 max-w-full whitespace-normal break-words rounded px-2.5 py-1.5 text-left text-sm font-semibold leading-5 ${mode === 'saved' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-white'}`}
    >
      {t('From teaching lists')}
    </button>
    <button
      type="button"
      onClick={() => onModeChange('paste')}
      className={`min-w-0 max-w-full whitespace-normal break-words rounded px-2.5 py-1.5 text-left text-sm font-semibold leading-5 ${mode === 'paste' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-white'}`}
    >
      {t('Paste new vocabulary')}
    </button>
    <button
      type="button"
      onClick={onCreateTeachingFolder}
      className="min-w-0 max-w-full whitespace-normal break-words rounded border border-gray-300 bg-white px-2.5 py-1.5 text-left text-sm font-semibold leading-5 text-gray-700 hover:bg-gray-100"
    >
      {t('New teaching folder')}
    </button>
  </div>
);

const addHistoryWords = (target, values, formatValue = (value) => (typeof value === 'string' ? value : value?.word)) => {
  (Array.isArray(values) ? values : []).forEach((value) => {
    const word = formatValue(value);
    if (word && !target.words.includes(word)) target.words.push(word);
  });
};

export const groupAssignmentHistory = (payload, studentId = '') => {
  const grouped = new Map();
  const rows = studentId
    ? (Array.isArray(payload?.vocab_mission) ? payload.vocab_mission : [])
    : (Array.isArray(payload) ? payload : []);

  rows.forEach((item) => {
    const date = item?.assigned_date || item?.date || '';
    const dueDate = item?.due_date || '';
    const modes = getTrainingModes(item);
    const key = `${date}::${dueDate}`;
    if (!grouped.has(key)) {
      grouped.set(key, {
        date,
        due_date: dueDate,
        training_mode: modes[0],
        training_modes: modes,
        words: [],
      });
    }
    const target = grouped.get(key);
    target.training_modes = normalizeTrainingModes([...target.training_modes, ...modes]);
    target.training_mode = target.training_modes[0];
    if (studentId) {
      const label = item?.definition_cn ? `${item.word} (${item.definition_cn})` : item?.word;
      addHistoryWords(target, [label]);
    } else {
      addHistoryWords(target, item?.words);
    }
  });

  return [...grouped.values()].sort((a, b) => (
    `${b.date}::${b.due_date}`.localeCompare(`${a.date}::${a.due_date}`)
  ));
};

const AssignWordsModal = ({ isOpen, onClose, classId, onWordsAssigned, student = null }) => {
  const { t } = useI18n();
  const studentId = student?._id || '';
  const studentName = student?.nickname || student?.english_name || student?.username || '';
  const [tab, setTab] = useState('assign');
  const [mode, setMode] = useState('saved');
  const [trainingModes, setTrainingModes] = useState([TRAINING_MODE_MEANING]);
  const [dueDate, setDueDate] = useState('');
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState('');
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState('');
  const [listPage, setListPage] = useState(1);
  const [listSearch, setListSearch] = useState('');
  const [listPagination, setListPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [savedEntryPage, setSavedEntryPage] = useState(1);
  const [savedList, setSavedList] = useState(null);
  const [savedSelections, setSavedSelections] = useState([]);
  const [rawText, setRawText] = useState('');
  const [review, setReview] = useState({ status: 'idle', preparationId: '', items: [] });
  const [meaningSelections, setMeaningSelections] = useState({});
  const [resultAction, setResultAction] = useState('assign');
  const [attachedPracticeTypes, setAttachedPracticeTypes] = useState([]);
  const [attachedReadingMode, setAttachedReadingMode] = useState('custom');
  const [attachedIeltsConfig, setAttachedIeltsConfig] = useState({
    question: '',
    part: '',
    suggested_duration_seconds: 90,
    strict_timer: false,
    target_band: IELTS_DEFAULT_TARGET_BAND,
  });
  const [destinationFolderId, setDestinationFolderId] = useState('');
  const [destinationListId, setDestinationListId] = useState('');
  const [destinationLists, setDestinationLists] = useState([]);
  const [newListTitle, setNewListTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activePreparationId, setActivePreparationId] = useState('');
  const [history, setHistory] = useState([]);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionDialog, setActionDialog] = useState(null);
  const validationAbortRef = useRef(null);
  const inputRevisionRef = useRef(0);
  const listRequestRef = useRef(0);

  const progressPreparationId = review.status === 'checking'
    ? review.preparationId
    : activePreparationId;
  const progress = useAssignmentGenerationProgress({
    active: Boolean(progressPreparationId),
    classId,
    preparationId: progressPreparationId,
    expectedPhase: review.status === 'checking' ? 'validation' : 'generation',
  });

  const loadFolders = useCallback(async () => {
    const { payload } = await requestJson('/api/vocabulary/folders?scope=mine');
    const next = Array.isArray(payload) ? payload : [];
    setFolders(next);
    setFolderId((current) => next.some((folder) => folder._id === current) ? current : (next[0]?._id || ''));
    setDestinationFolderId((current) => next.some((folder) => folder._id === current) ? current : (next[0]?._id || ''));
  }, []);

  const loadLists = useCallback(async (selectedFolderId, requestedPage = 1, search = '') => {
    const requestId = listRequestRef.current + 1;
    listRequestRef.current = requestId;
    if (!selectedFolderId) {
      setLists([]);
      setListId('');
      setListPagination({ page: 1, pages: 1, total: 0 });
      return;
    }
    const query = new URLSearchParams({
      page: String(requestedPage),
      page_size: String(TEACHING_LIST_PAGE_SIZE),
    });
    if (search.trim()) query.set('search', search.trim());
    const { payload } = await requestJson(`/api/vocabulary/folders/${selectedFolderId}/lists?${query.toString()}`);
    if (requestId !== listRequestRef.current) return;
    const next = Array.isArray(payload) ? payload : (Array.isArray(payload?.items) ? payload.items : []);
    setLists(next);
    setListPagination({
      page: Number(payload?.page || requestedPage || 1),
      pages: Number(payload?.pages || 1),
      total: Number(payload?.total ?? next.length),
    });
    setListId((current) => next.some((list) => list._id === current) ? current : (next[0]?._id || ''));
  }, []);

  const reset = useCallback(() => {
    validationAbortRef.current?.abort();
    validationAbortRef.current = null;
    inputRevisionRef.current += 1;
    setTab('assign');
    setMode('saved');
    setTrainingModes([TRAINING_MODE_MEANING]);
    setDueDate('');
    setListId('');
    setListPage(1);
    setListSearch('');
    setListPagination({ page: 1, pages: 1, total: 0 });
    setSavedEntryPage(1);
    setSavedList(null);
    setSavedSelections([]);
    setRawText('');
    setReview({ status: 'idle', preparationId: '', items: [] });
    setMeaningSelections({});
    setResultAction('assign');
    setAttachedPracticeTypes([]);
    setAttachedReadingMode('custom');
    setAttachedIeltsConfig({ question: '', part: '', suggested_duration_seconds: 90, strict_timer: false, target_band: IELTS_DEFAULT_TARGET_BAND });
    setDestinationListId('');
    setDestinationLists([]);
    setNewListTitle('');
    setSubmitting(false);
    setActivePreparationId('');
    setHistory([]);
    setHistoryRefresh(0);
    setError('');
    setActionDialog(null);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    reset();
    setLoading(true);
    loadFolders().catch((loadError) => setError(loadError.message)).finally(() => setLoading(false));
  }, [isOpen, loadFolders, reset]);

  useEffect(() => {
    if (!isOpen || mode !== 'saved') return;
    loadLists(folderId, listPage, listSearch).catch((loadError) => setError(loadError.message));
  }, [isOpen, mode, folderId, listPage, listSearch, loadLists]);

  useEffect(() => {
    if (!isOpen || mode !== 'saved' || !listId) {
      setSavedList(null);
      setSavedSelections([]);
      return;
    }
    setLoading(true);
    requestJson(`/api/vocabulary/lists/${listId}`)
      .then(({ payload }) => {
        setSavedList(payload);
        setSavedEntryPage(1);
        setSavedSelections((payload.entries || []).map(entryKey));
      })
      .catch((loadError) => setError(loadError.message))
      .finally(() => setLoading(false));
  }, [isOpen, mode, listId]);

  useEffect(() => {
    if (review.status !== 'checking' || progress?.phase !== 'validation') return;
    if (progress.state === 'failed') {
      setReview((current) => ({ ...current, status: 'error' }));
      setError(progress.message || t('This list could not be checked.'));
      return;
    }
    if (progress.state !== 'ready') return;
    const items = Array.isArray(progress.items) ? progress.items : [];
    setReview((current) => ({ ...current, status: 'ready', items }));
    setMeaningSelections(initialMeaningSelections(items));
  }, [progress, review.status, t]);

  useEffect(() => {
    if (!isOpen || tab !== 'history') return;
    setLoading(true);
    const historyUrl = studentId
      ? `/api/classes/${classId}/students/${studentId}/vocab-mission-history`
      : `/api/classes/${classId}/assignment-history`;
    requestJson(historyUrl)
      .then(({ payload }) => {
        setHistory(groupAssignmentHistory(payload, studentId));
      })
      .catch((loadError) => setError(loadError.message))
      .finally(() => setLoading(false));
  }, [isOpen, tab, classId, studentId, historyRefresh]);

  const selectedMeaningCount = useMemo(
    () => countValidMeaningSelections(review.items, meaningSelections),
    [review.items, meaningSelections],
  );

  const primaryTrainingMode = getPrimaryTrainingMode(trainingModes);

  const savedEntryPagination = useMemo(
    () => paginateItems(savedList?.entries || [], savedEntryPage, SAVED_ENTRY_PAGE_SIZE),
    [savedList, savedEntryPage],
  );

  useEffect(() => {
    if (!destinationFolderId) {
      setDestinationLists([]);
      return;
    }
    requestJson(`/api/vocabulary/folders/${destinationFolderId}/lists`)
      .then(({ payload }) => {
        const next = Array.isArray(payload) ? payload : [];
        setDestinationLists(next);
        setDestinationListId((current) => next.some((list) => list._id === current) ? current : (next[0]?._id || ''));
      })
      .catch((loadError) => setError(loadError.message));
  }, [destinationFolderId]);

  const createTeachingFolder = () => {
    setActionDialog({
      kind: 'text',
      title: 'New teaching folder',
      inputLabel: 'Teaching folder name',
      confirmLabel: 'Create folder',
      onConfirm: async (title) => {
      const { payload } = await requestJson('/api/vocabulary/folders', {
        method: 'POST',
        body: JSON.stringify({ title }),
      });
      await loadFolders();
      setFolderId(payload._id);
      setDestinationFolderId(payload._id);
      },
    });
  };

  const renameTeachingFolder = () => {
    const folder = folders.find((item) => item._id === folderId);
    if (!folder) return;
    setActionDialog({
      kind: 'text',
      title: 'Rename folder',
      inputLabel: 'Teaching folder name',
      initialValue: folder.title,
      confirmLabel: 'Save changes',
      onConfirm: async (title) => {
        if (title === folder.title) return;
        await requestJson(`/api/vocabulary/folders/${folder._id}`, {
          method: 'PATCH',
          body: JSON.stringify({ title }),
        });
        await loadFolders();
      },
    });
  };

  const archiveTeachingFolder = () => {
    const folder = folders.find((item) => item._id === folderId);
    if (!folder) return;
    setActionDialog({
      kind: 'confirm',
      title: 'Archive teaching folder',
      description: 'Archive this teaching folder and its lists?',
      confirmLabel: 'Archive',
      destructive: true,
      onConfirm: async () => {
        await requestJson(`/api/vocabulary/folders/${folder._id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'archived' }),
        });
        setFolderId('');
        setListId('');
        setSavedList(null);
        await loadFolders();
      },
    });
  };

  const renameTeachingList = () => {
    if (!savedList) return;
    setActionDialog({
      kind: 'text',
      title: 'Rename list',
      inputLabel: 'List name',
      initialValue: savedList.title,
      confirmLabel: 'Save changes',
      onConfirm: async (title) => {
        if (title === savedList.title) return;
        const { payload } = await requestJson(`/api/vocabulary/lists/${savedList._id}`, {
          method: 'PUT',
          body: JSON.stringify({ title }),
        });
        setSavedList((current) => ({ ...current, title: payload.title }));
        await loadLists(folderId, listPage, listSearch);
        setListId(savedList._id);
      },
    });
  };

  const archiveTeachingList = () => {
    if (!savedList) return;
    setActionDialog({
      kind: 'confirm',
      title: 'Archive teaching list',
      description: 'Archive this teaching list?',
      confirmLabel: 'Archive',
      destructive: true,
      onConfirm: async () => {
        await requestJson(`/api/vocabulary/lists/${savedList._id}`, { method: 'DELETE' });
        setSavedList(null);
        setListId('');
        await loadLists(folderId, listPage, listSearch);
        await loadFolders();
      },
    });
  };

  const close = () => {
    reset();
    onClose();
  };

  const cancelPreparation = useCallback((preparationId) => {
    if (!preparationId) return;
    fetch(`/api/classes/${classId}/assignment-preparations/${preparationId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    }).catch(() => {});
  }, [classId]);

  const handleRawTextChange = (event) => {
    const nextText = event.target.value;
    const preparationId = review.preparationId || activePreparationId;
    validationAbortRef.current?.abort();
    validationAbortRef.current = null;
    inputRevisionRef.current += 1;
    cancelPreparation(preparationId);
    setRawText(nextText);
    setReview({ status: 'idle', preparationId: '', items: [] });
    setMeaningSelections({});
    setActivePreparationId('');
    setSubmitting(false);
    setError('');
  };

  const handleTrainingModesChange = (nextModes) => {
    const normalizedModes = normalizeTrainingModes(nextModes);
    const nextPrimaryMode = getPrimaryTrainingMode(normalizedModes);
    const changed = normalizedModes.length !== trainingModes.length
      || normalizedModes.some((candidate, index) => candidate !== trainingModes[index]);
    if (!changed) return;

    if (nextPrimaryMode !== primaryTrainingMode) {
      validationAbortRef.current?.abort();
      validationAbortRef.current = null;
      inputRevisionRef.current += 1;
      cancelPreparation(review.preparationId || activePreparationId);
      setActivePreparationId('');
      setSubmitting(false);
      if (mode === 'paste' && review.status !== 'idle') {
        setReview({ status: 'idle', preparationId: '', items: [] });
        setMeaningSelections({});
      }
      setError('');
    }
    setTrainingModes(normalizedModes);
  };

  const preparePaste = async () => {
    if (!rawText.trim()) {
      setError(t('Enter at least one word.'));
      return;
    }
    setError('');
    setSubmitting(true);
    const revision = inputRevisionRef.current;
    const sourceText = rawText;
    validationAbortRef.current?.abort();
    const controller = new AbortController();
    validationAbortRef.current = controller;
    try {
      const response = await fetch(`/api/classes/${classId}/validate-words`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ raw_text: sourceText, async: true, training_mode: primaryTrainingMode }),
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => ({}));
      if (revision !== inputRevisionRef.current) {
        cancelPreparation(payload.preparation_id);
        return;
      }
      if (!response.ok) throw new Error(payload.message || t('This list could not be checked.'));
      if (response.status === 202 && payload.preparation_id) {
        setReview({ status: 'checking', preparationId: payload.preparation_id, items: [] });
        return;
      }
      const items = Array.isArray(payload.items) ? payload.items : [];
      setReview({ status: 'ready', preparationId: payload.preparation_id || '', items });
      setMeaningSelections(initialMeaningSelections(items));
    } catch (requestError) {
      if (requestError.name === 'AbortError' || revision !== inputRevisionRef.current) return;
      setError(requestError.message);
      setReview({ status: 'error', preparationId: '', items: [] });
    } finally {
      if (validationAbortRef.current === controller) validationAbortRef.current = null;
      if (revision === inputRevisionRef.current) setSubmitting(false);
    }
  };

  const retryItem = async (item) => {
    if (!review.preparationId) return;
    setReview((current) => ({
      ...current,
      items: current.items.map((candidate) => candidate.entry_id === item.entry_id
        ? { ...candidate, inspection_status: 'retrying' }
        : candidate),
    }));
    try {
      await requestJson(`/api/classes/${classId}/assignment-preparations/${review.preparationId}/items/${item.entry_id}/retry`, {
        method: 'POST',
        body: JSON.stringify({ source_text: item.retry_source_text || item.source_text || item.word }),
      });
      for (let attempt = 0; attempt < 45; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        const { payload } = await requestJson(`/api/classes/${classId}/assignment-preparations/${review.preparationId}/progress`);
        const replacement = (payload.items || []).find((candidate) => candidate.entry_id === item.entry_id);
        if (replacement && replacement.inspection_status !== 'retrying') {
          const items = payload.items || [];
          setReview((current) => ({ ...current, items }));
          setMeaningSelections((current) => ({
            ...current,
            [item.entry_id]: initialMeaningSelections([replacement])[item.entry_id] || [],
          }));
          return;
        }
      }
      throw new Error(t('This entry is still being checked.'));
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const createDestinationList = async () => {
    const title = newListTitle.trim();
    if (!destinationFolderId || !title) throw new Error(t('Choose a folder and give the list a name.'));
    const { payload } = await requestJson(`/api/vocabulary/folders/${destinationFolderId}/lists`, {
      method: 'POST',
      body: JSON.stringify({ title, entries: [] }),
    });
    return payload._id;
  };

  const completePreparedAssignment = async ({ preparationId, selections, saveListId = '', saveOnly = false }) => {
    const trainingModePayload = buildTrainingModePayload(trainingModes);
    const practiceOptions = attachedPracticeTypes.map((practice_type) => ({
      practice_type,
      config: practice_type === 'adaptive_reading'
        ? { reading_mode: attachedReadingMode }
        : practice_type === 'ielts_speaking'
          ? serializeIeltsSpeakingConfig(attachedIeltsConfig?.question?.trim()
            ? {
              ...attachedIeltsConfig,
              format_version: 2,
              simulation_mode: 'custom',
              strict_timer: true,
            }
            : attachedIeltsConfig)
          : {},
    }));
    setActivePreparationId(preparationId);
    const assignmentUrl = studentId && !saveOnly
      ? `/api/classes/${classId}/students/${studentId}/assign-words`
      : `/api/classes/${classId}/assign-words`;
    const response = await fetch(assignmentUrl, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        preparation_id: preparationId,
        selections,
        due_date: dueDate || null,
        ...trainingModePayload,
        save_list_id: saveListId || undefined,
        save_only: saveOnly,
        practice_options: saveOnly ? [] : practiceOptions,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (response.status === 202) {
      const finalProgress = await waitForAssignmentMaterialization({
        classId,
        preparationId,
        token: localStorage.getItem('token'),
      });
      const completed = Number(finalProgress.assigned_completed || 0);
      const failed = Number(finalProgress.failed_count || 0);
      if (!saveOnly && attachedPracticeTypes.length) {
        const practiceResponse = await fetch('/api/admin/practice-assignments', {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            preparation_id: preparationId,
            class_id: classId,
            ...(studentId ? { student_id: studentId } : {}),
            practice_options: practiceOptions,
          }),
        });
        const practicePayload = await practiceResponse.json().catch(() => ({}));
        if (!practiceResponse.ok) {
          return t('{count} meanings are ready, but the attached practice needs attention.', { count: completed });
        }
        const assignedCount = Array.isArray(practicePayload.assignments) ? practicePayload.assignments.length : attachedPracticeTypes.length;
        return t('{count} meanings are ready and {practices} practices were assigned.', { count: completed, practices: assignedCount });
      }
      return saveOnly
        ? t('Saved {count} exact meanings to the teaching list.', { count: completed })
        : (failed
          ? t('{count} meanings are ready; {failed} need another try.', { count: completed, failed })
          : t('{count} meanings are ready for students.', { count: completed }));
    }
    if (!response.ok) throw new Error(payload.message || t('Assignment failed.'));
    return payload.message || t('Done.');
  };

  const assignSavedList = async () => {
    const entries = (savedList?.entries || []).filter((entry) => savedSelections.includes(entryKey(entry)));
    if (!entries.length) {
      setError(t('Choose at least one meaning to assign.'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const { payload } = await requestJson(`/api/vocabulary/lists/${savedList._id}/prepare-assignment`, {
        method: 'POST',
        body: JSON.stringify({
          class_id: classId,
          training_mode: primaryTrainingMode,
          entries: entries.map(({ word, sense_id }) => ({ word, sense_id })),
        }),
      });
      const message = await completePreparedAssignment({
        preparationId: payload.preparation_id,
        selections: payload.selections,
      });
      onWordsAssigned?.(message);
      close();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
      setActivePreparationId('');
    }
  };

  const finishPaste = async () => {
    const selections = validMeaningSelections(review.items, meaningSelections);
    if (!selections.some((item) => item.sense_ids.length)) {
      setError(t('Choose at least one meaning.'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      let saveListId = '';
      if (resultAction === 'new_assign' || resultAction === 'save') saveListId = await createDestinationList();
      if (resultAction === 'append_assign') {
        if (!destinationListId) throw new Error(t('Choose a teaching list.'));
        saveListId = destinationListId;
      }
      const message = await completePreparedAssignment({
        preparationId: review.preparationId,
        selections,
        saveListId,
        saveOnly: resultAction === 'save',
      });
      onWordsAssigned?.(message);
      close();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
      setActivePreparationId('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4">
      <div className="flex h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl sm:h-[90vh]">
        <header className="flex items-center justify-between border-b px-4 py-3 sm:px-6">
          <div>
            <h2 className="text-xl font-bold text-gray-950">
              {studentId ? t('Assign vocabulary to {name}', { name: studentName }) : t('Assign vocabulary')}
            </h2>
            <p className="text-sm text-gray-500">
              {studentId
                ? t('Only this student will receive the assignment. Teaching lists remain reusable.')
                : t('Reuse a teaching list or review new vocabulary before assigning it.')}
            </p>
          </div>
          <button type="button" onClick={close} className="p-2 text-gray-500 hover:text-gray-900" title={t('Close')}><XMarkIcon className="h-6 w-6" /></button>
        </header>

        <div className="flex border-b bg-gray-50 px-4 sm:px-6">
          <button type="button" onClick={() => setTab('assign')} className={`border-b-2 px-3 py-3 text-sm font-semibold ${tab === 'assign' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600'}`}>{t('Assign')}</button>
          <button type="button" onClick={() => setTab('history')} className={`border-b-2 px-3 py-3 text-sm font-semibold ${tab === 'history' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600'}`}>{t('History')}</button>
        </div>

        {error && <p role="alert" className="mx-4 mt-3 border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800 sm:mx-6">{error}</p>}

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {tab === 'history' ? (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-bold text-gray-950">{studentId ? t('Recent assignments for this student') : t('Recent class assignments')}</h3>
                <button type="button" onClick={() => setHistoryRefresh((value) => value + 1)} className="inline-flex items-center gap-2 rounded border px-3 py-2 text-sm"><ArrowPathIcon className="h-4 w-4" />{t('Refresh')}</button>
              </div>
              {loading && <p>{t('Loading...')}</p>}
              <div className="space-y-3">
                {history.map((item, index) => (
                  <section key={`${item.date || ''}:${index}`} className="rounded border border-gray-200 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <strong>{t('Assigned {date}', { date: item.date || '' })}</strong>
                      <TrainingModeBadge mode={item.training_modes || item.training_mode} />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">{item.due_date ? t('Due {date}', { date: item.due_date }) : t('No deadline')}</p>
                    <div className="mt-3 flex flex-wrap gap-2">{(item.words || []).map((word) => <span data-i18n-skip key={word} className="rounded border bg-gray-50 px-2 py-1 text-sm">{word}</span>)}</div>
                  </section>
                ))}
                {!loading && !history.length && <p className="text-gray-500">{t('No history yet.')}</p>}
              </div>
            </div>
          ) : (
            <>
              <div className="mb-5 grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
                <AssignmentSourceToolbar
                  mode={mode}
                  onModeChange={setMode}
                  onCreateTeachingFolder={createTeachingFolder}
                  t={t}
                />
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-gray-800">
                    {t('Deadline')}
                    <input type="date" min={new Date().toLocaleDateString('en-CA')} value={dueDate} onChange={(event) => setDueDate(event.target.value)} className="mt-1 w-full rounded border border-gray-300 p-2" />
                    <span className="mt-1 block text-xs font-normal text-gray-500">{dueDate ? t('Due {date}', { date: dueDate }) : t('No deadline')}</span>
                  </label>
                  <TrainingModeSelector
                    value={trainingModes}
                    onChange={handleTrainingModesChange}
                    legend="Batch training mode"
                    name="batch-training-mode"
                    disabled={submitting || loading}
                  />
                </div>
              </div>

              {mode === 'saved' ? (
                <div className="grid gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
                  <aside className="space-y-4">
                    <label className="block text-sm font-medium text-gray-800">
                      {t('Folder')}
                      <select value={folderId} onChange={(event) => { setFolderId(event.target.value); setListPage(1); setListSearch(''); }} className="mt-1 w-full rounded border border-gray-300 bg-white p-2.5">
                        {folders.map((folder) => <option data-i18n-skip key={folder._id} value={folder._id}>{folder.title} ({folder.list_count || 0})</option>)}
                      </select>
                    </label>
                    {folderId && (
                      <div className="flex gap-2">
                        <button type="button" onClick={renameTeachingFolder} title={t('Rename folder')} className="rounded border border-gray-300 p-2 text-gray-600 hover:bg-gray-100"><PencilSquareIcon className="h-5 w-5" /></button>
                        <button type="button" onClick={archiveTeachingFolder} title={t('Archive folder')} className="rounded border border-red-200 p-2 text-red-600 hover:bg-red-50"><ArchiveBoxIcon className="h-5 w-5" /></button>
                      </div>
                    )}
                    <label className="block text-sm font-medium text-gray-800">
                      {t('Search teaching lists')}
                      <input
                        type="search"
                        value={listSearch}
                        onChange={(event) => { setListSearch(event.target.value); setListPage(1); }}
                        className="mt-1 w-full rounded border border-gray-300 p-2.5"
                        placeholder={t('Search by list name')}
                      />
                    </label>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{t('{count} lists', { count: listPagination.total })}</span>
                      {listPagination.pages > 1 && <span>{t('Page {page} of {pages}', { page: listPagination.page, pages: listPagination.pages })}</span>}
                    </div>
                    <div className="space-y-2">
                      {lists.map((list) => (
                        <button type="button" key={list._id} onClick={() => setListId(list._id)} className={`w-full rounded border p-3 text-left ${list._id === listId ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                          <span className="flex items-center gap-2"><FolderIcon className="h-4 w-4" /><strong data-i18n-skip>{list.title}</strong></span>
                          <span className="mt-1 block text-xs text-gray-500">{t('{count} meanings', { count: list.entry_count || 0 })}</span>
                        </button>
                      ))}
                      {!loading && !lists.length && <p className="rounded border border-dashed border-gray-300 p-3 text-sm text-gray-500">{listSearch ? t('No teaching lists match your search.') : t('No teaching lists yet.')}</p>}
                    </div>
                    {listPagination.pages > 1 && (
                      <div className="flex items-center justify-between gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => setListPage((current) => Math.max(1, current - 1))}
                          disabled={listPagination.page <= 1 || loading}
                          className="inline-flex items-center gap-1 rounded border border-gray-300 px-2.5 py-2 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                          title={t('Previous page')}
                        >
                          <ChevronLeftIcon className="h-4 w-4" />{t('Previous')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setListPage((current) => Math.min(listPagination.pages, current + 1))}
                          disabled={listPagination.page >= listPagination.pages || loading}
                          className="inline-flex items-center gap-1 rounded border border-gray-300 px-2.5 py-2 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                          title={t('Next page')}
                        >
                          {t('Next')}<ChevronRightIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </aside>
                  <section>
                    {loading && <p>{t('Loading...')}</p>}
                    {savedList && (
                      <>
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <div><h3 data-i18n-skip className="text-lg font-bold">{savedList.title}</h3><p className="text-xs text-gray-500">{t('Choose the exact meanings for this assignment.')}</p></div>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => setSavedSelections(savedSelections.length === (savedList.entries || []).length ? [] : (savedList.entries || []).map(entryKey))} className="rounded border px-3 py-2 text-sm">{savedSelections.length === (savedList.entries || []).length ? t('Clear all') : t('Select all')}</button>
                            <button type="button" onClick={renameTeachingList} title={t('Rename list')} className="rounded border border-gray-300 p-2 text-gray-600 hover:bg-gray-100"><PencilSquareIcon className="h-5 w-5" /></button>
                            <button type="button" onClick={archiveTeachingList} title={t('Archive list')} className="rounded border border-red-200 p-2 text-red-600 hover:bg-red-50"><ArchiveBoxIcon className="h-5 w-5" /></button>
                          </div>
                        </div>
                        {savedEntryPagination.pages > 1 && (
                          <div className="mb-2 flex items-center justify-between gap-2 text-xs text-gray-600">
                            <span>{t('Page {page} of {pages}', { page: savedEntryPagination.page, pages: savedEntryPagination.pages })}</span>
                            <div className="flex items-center gap-1">
                              <button type="button" onClick={() => setSavedEntryPage((current) => Math.max(1, current - 1))} disabled={savedEntryPagination.page <= 1} className="rounded border border-gray-300 p-1.5 disabled:cursor-not-allowed disabled:opacity-40" title={t('Previous page')} aria-label={t('Previous page')}>
                                <ChevronLeftIcon className="h-4 w-4" />
                              </button>
                              <button type="button" onClick={() => setSavedEntryPage((current) => Math.min(savedEntryPagination.pages, current + 1))} disabled={savedEntryPagination.page >= savedEntryPagination.pages} className="rounded border border-gray-300 p-1.5 disabled:cursor-not-allowed disabled:opacity-40" title={t('Next page')} aria-label={t('Next page')}>
                                <ChevronRightIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="overflow-hidden rounded border border-gray-200">
                          {savedEntryPagination.items.map((entry) => {
                            const key = entryKey(entry);
                            return (
                              <label key={key} className={`grid cursor-pointer gap-2 border-b p-3 last:border-b-0 sm:grid-cols-[auto_150px_minmax(0,1fr)] ${savedSelections.includes(key) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                                <input type="checkbox" checked={savedSelections.includes(key)} onChange={() => setSavedSelections((current) => current.includes(key) ? current.filter((value) => value !== key) : [...current, key])} className="mt-1 h-4 w-4 text-blue-600" />
                                <strong data-i18n-skip>{entry.word}</strong>
                                <span data-i18n-skip className="text-sm text-gray-700">{entry.definition_cn || entry.definition_en}</span>
                              </label>
                            );
                          })}
                        </div>
                        {savedEntryPagination.pages > 1 && (
                          <div className="mt-2 flex justify-end text-xs text-gray-600">
                            <span>{t('Page {page} of {pages}', { page: savedEntryPagination.page, pages: savedEntryPagination.pages })}</span>
                          </div>
                        )}
                        <PracticeChecklist selected={attachedPracticeTypes} onChange={(value) => setAttachedPracticeTypes((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value])} disabled={submitting} readingMode={attachedReadingMode} onReadingModeChange={setAttachedReadingMode} ieltsConfig={attachedIeltsConfig} onIeltsConfigChange={(value) => setAttachedIeltsConfig((current) => ({ ...current, ...value }))} t={t} />
                        <div className="mt-4 flex justify-end">
                          <button type="button" onClick={assignSavedList} disabled={submitting || !savedSelections.length} className="rounded bg-blue-600 px-5 py-2.5 font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{submitting ? t('Preparing...') : t('Assign {count} meanings', { count: savedSelections.length })}</button>
                        </div>
                      </>
                    )}
                  </section>
                </div>
              ) : (
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-gray-800">
                    {t('Words or words with meanings')}
                    <textarea value={rawText} onChange={handleRawTextChange} className="mt-1 min-h-44 w-full rounded border border-gray-300 p-3 leading-7" placeholder={'draft 草稿\ndraft 征兵\nlight 轻的'} />
                  </label>
                  <div className="flex justify-end"><button type="button" onClick={preparePaste} disabled={submitting || review.status === 'checking' || !rawText.trim()} className="rounded bg-blue-600 px-5 py-2.5 font-semibold text-white disabled:opacity-50">{submitting || review.status === 'checking' ? t('Checking...') : t('Check words and meanings')}</button></div>
                  {review.status === 'checking' ? (
                    <p className="text-blue-800">{t('Checking words and meanings...')}</p>
                  ) : review.status === 'ready' ? (
                    <>
                      <AssignmentMeaningReview items={review.items} selections={meaningSelections} onSelectionChange={(entryId, senseId, checked) => setMeaningSelections((current) => toggleMeaningSelection(current, entryId, senseId, checked))} onRetry={retryItem} resetKey={inputRevisionRef.current} />
                      <section className="border border-gray-200 bg-gray-50 p-4">
                        <h3 className="font-bold text-gray-950">{t('What should happen after review?')}</h3>
                        <div className="mt-3 grid gap-2 md:grid-cols-2">
                          {RESULT_ACTIONS.map(([value, label, description]) => (
                            <label key={value} className={`cursor-pointer rounded border p-3 ${resultAction === value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                              <input type="radio" name="result-action" value={value} checked={resultAction === value} onChange={() => setResultAction(value)} className="mr-2 text-blue-600" />
                              <strong>{t(label)}</strong>
                              <span className="mt-1 block pl-6 text-xs text-gray-500">{t(description)}</span>
                            </label>
                          ))}
                        </div>
                        {resultAction !== 'assign' && (
                          <div className="mt-4 grid gap-3 md:grid-cols-2">
                            <label className="text-sm font-medium text-gray-800">
                              {t('Teaching folder')}
                              <select value={destinationFolderId} onChange={(event) => { setDestinationFolderId(event.target.value); setDestinationListId(''); }} className="mt-1 w-full rounded border border-gray-300 bg-white p-2.5">
                                {folders.map((folder) => <option data-i18n-skip key={folder._id} value={folder._id}>{folder.title}</option>)}
                              </select>
                            </label>
                            {resultAction === 'append_assign' ? (
                              <label className="text-sm font-medium text-gray-800">
                                {t('Teaching list')}
                                <select value={destinationListId} onChange={(event) => setDestinationListId(event.target.value)} className="mt-1 w-full rounded border border-gray-300 bg-white p-2.5">
                                  <option value="">{t('Choose a list')}</option>
                                  {destinationLists.map((list) => <option data-i18n-skip key={list._id} value={list._id}>{list.title}</option>)}
                                </select>
                              </label>
                            ) : (
                              <label className="text-sm font-medium text-gray-800">
                                {t('New list name')}
                                <input value={newListTitle} onChange={(event) => setNewListTitle(event.target.value)} className="mt-1 w-full rounded border border-gray-300 p-2.5" placeholder={t('For example: Unit 3 vocabulary')} />
                              </label>
                            )}
                          </div>
                        )}
                        {resultAction !== 'save' && (
                          <PracticeChecklist selected={attachedPracticeTypes} onChange={(value) => setAttachedPracticeTypes((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value])} disabled={submitting} readingMode={attachedReadingMode} onReadingModeChange={setAttachedReadingMode} ieltsConfig={attachedIeltsConfig} onIeltsConfigChange={(value) => setAttachedIeltsConfig((current) => ({ ...current, ...value }))} t={t} />
                        )}
                        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                          <button type="button" onClick={() => setReview({ status: 'idle', preparationId: '', items: [] })} className="rounded border border-gray-300 bg-white px-4 py-2">{t('Edit input')}</button>
                          <button type="button" onClick={finishPaste} disabled={submitting || selectedMeaningCount === 0} className="rounded bg-blue-600 px-5 py-2.5 font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{submitting ? t('Preparing...') : t('Continue with {count} meanings', { count: selectedMeaningCount })}</button>
                        </div>
                      </section>
                    </>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>

        <AssignmentGenerationProgress active={Boolean(progressPreparationId)} progress={progress} phase={review.status === 'checking' ? 'validation' : 'generation'} />
        <footer className="flex justify-end border-t bg-gray-50 px-4 py-3 sm:px-6"><button type="button" onClick={close} className="rounded border border-gray-300 bg-white px-4 py-2 text-gray-800 hover:bg-gray-100">{t('Close')}</button></footer>
        <VocabularyActionDialog dialog={actionDialog} onClose={() => setActionDialog(null)} />
      </div>
    </div>
  );
};

export default AssignWordsModal;
